import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { Plus, Minus, Clock, X, Droplets, UtensilsCrossed, Sunrise, Sun, Moon, Loader2, Target } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

interface MealItem {
  id: string;
  name: string;
  name_bn?: string;
  calories: number;
  logged_at: string;
  meal_type: string;
  quantity: number;
}

interface CalorieTarget {
  min: number;
  max: number;
}

interface SuggestedMeal {
  name: string;
  nameBn: string;
  calories: number;
}

const normalMealTypes = [
  { key: "breakfast", labelEn: "Breakfast", labelBn: "সকালের নাস্তা" },
  { key: "lunch", labelEn: "Lunch", labelBn: "দুপুরের খাবার" },
  { key: "snack", labelEn: "Snack", labelBn: "নাস্তা" },
  { key: "dinner", labelEn: "Dinner", labelBn: "রাতের খাবার" },
];

const fastingMealTypes = [
  { key: "sehri", labelEn: "Sehri", labelBn: "সেহরি" },
  { key: "iftar", labelEn: "Iftaar", labelBn: "ইফতার" },
  { key: "dinner", labelEn: "Dinner", labelBn: "রাতের খাবার" },
];

const mealTypeIcons: Record<string, React.ElementType> = {
  breakfast: Sunrise, lunch: Sun, snack: UtensilsCrossed, dinner: Moon, sehri: Moon, iftar: Sunrise,
};

const allFoods: SuggestedMeal[] = [
  { name: "Rice", nameBn: "ভাত", calories: 250 },
  { name: "Polao", nameBn: "পোলাও", calories: 400 },
  { name: "Biryani", nameBn: "বিরিয়ানি", calories: 650 },
  { name: "Fried Rice", nameBn: "ফ্রাইড রাইস", calories: 500 },
  { name: "Khichuri", nameBn: "খিচুড়ি", calories: 320 },
  { name: "Paratha", nameBn: "পরোটা", calories: 200 },
  { name: "Roti", nameBn: "রুটি", calories: 100 },
  { name: "Chicken Curry", nameBn: "মুরগির তরকারি", calories: 300 },
  { name: "Beef Curry", nameBn: "গরুর মাংস", calories: 350 },
  { name: "Fish Curry", nameBn: "মাছের ঝোল", calories: 230 },
  { name: "Dal", nameBn: "ডাল", calories: 150 },
  { name: "Mixed Vegetables", nameBn: "মিক্সড সবজি", calories: 120 },
  { name: "Egg Curry", nameBn: "ডিমের তরকারি", calories: 180 },
  { name: "Boiled Egg", nameBn: "সেদ্ধ ডিম", calories: 70 },
  { name: "Milk", nameBn: "দুধ", calories: 150 },
  { name: "Tea", nameBn: "চা", calories: 50 },
  { name: "Banana", nameBn: "কলা", calories: 105 },
  { name: "Apple", nameBn: "আপেল", calories: 95 },
  { name: "Mango", nameBn: "আম", calories: 100 },
  { name: "Singara", nameBn: "সিঙ্গাড়া", calories: 180 },
  { name: "Fuchka", nameBn: "ফুচকা", calories: 200 },
  { name: "Dates", nameBn: "খেজুর", calories: 28 },
  { name: "Haleem", nameBn: "হালিম", calories: 400 },
  { name: "Rosogolla", nameBn: "রসগোল্লা", calories: 125 },
  { name: "Salad", nameBn: "সালাদ", calories: 50 },
];

const MealsPage = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const bn = language === "bn";
  const [fastingMode, setFastingMode] = useState(false);
  const mealTypes = fastingMode ? fastingMealTypes : normalMealTypes;
  const [activeMealType, setActiveMealType] = useState("lunch");
  const [meals, setMeals] = useState<MealItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [waterGlasses, setWaterGlasses] = useState(0);
  const waterGoal = 8;
  const [calorieTarget, setCalorieTarget] = useState<CalorieTarget>({ min: 0, max: 0 });

  const [showCustom, setShowCustom] = useState(false);
  const [customName, setCustomName] = useState("");
  const [customCal, setCustomCal] = useState("");

  useEffect(() => {
    if (!user) return;
    const uid = user.id;
    const today = new Date().toISOString().split("T")[0];

    Promise.all([
      supabase.from("meals").select("*").eq("patient_id", uid).gte("logged_at", today).order("logged_at", { ascending: false }),
      supabase.from("water_intake").select("*").eq("patient_id", uid).eq("date", today).maybeSingle(),
      supabase.from("calorie_targets").select("*").eq("patient_id", uid).order("updated_at", { ascending: false }).limit(1).maybeSingle(),
      supabase.from("profiles").select("height").eq("user_id", uid).single(),
    ]).then(([mealsRes, waterRes, ctRes, profileRes]) => {
      setMeals((mealsRes.data ?? []).map(m => ({ ...m, quantity: m.quantity || 1 })));
      setWaterGlasses(waterRes.data?.glasses ?? 0);
      if (ctRes.data && ctRes.data.max_calories > 0) {
        setCalorieTarget({ min: ctRes.data.min_calories, max: ctRes.data.max_calories });
      } else {
        const heightCm = profileRes.data?.height ?? 0;
        if (heightCm > 0) {
          const hm = heightCm / 100;
          const hSq = hm * hm;
          setCalorieTarget({
            min: Math.round(hSq * 18.5 * 30),
            max: Math.round(hSq * 24.9 * 30),
          });
        }
      }
    }).finally(() => setLoading(false));
  }, [user]);

  const handleFastingToggle = (val: boolean) => {
    setFastingMode(val);
    setActiveMealType(val ? "sehri" : "breakfast");
  };

  const currentMeals = meals.filter(m => m.meal_type === activeMealType);
  const dayTotalCal = meals.reduce((s, m) => s + m.calories * m.quantity, 0);

  const addSuggestedMeal = async (suggestion: SuggestedMeal) => {
    if (!user) return;
    const newMeal = {
      name: suggestion.name,
      name_bn: suggestion.nameBn,
      calories: suggestion.calories,
      meal_type: activeMealType,
      quantity: 1,
      patient_id: user.id,
    };
    const { data, error } = await supabase.from("meals").insert(newMeal).select().single();
    if (error) {
      toast.error(error.message);
      return;
    }
    if (data) setMeals(prev => [data, ...prev]);
  };

  const addCustomMeal = async () => {
    if (!customName.trim() || !customCal.trim() || !user) return;
    const newMeal = {
      name: customName.trim(),
      calories: parseInt(customCal) || 0,
      meal_type: activeMealType,
      quantity: 1,
      patient_id: user.id,
    };
    const { data, error } = await supabase.from("meals").insert(newMeal).select().single();
    if (error) {
      toast.error(error.message);
      return;
    }
    if (data) setMeals(prev => [data, ...prev]);
    setCustomName("");
    setCustomCal("");
    setShowCustom(false);
  };

  const removeMeal = async (id: string) => {
    const removed = meals.find((meal) => meal.id === id);
    setMeals(prev => prev.filter(m => m.id !== id));
    const { error } = await supabase.from("meals").delete().eq("id", id);
    if (error) {
      if (removed) setMeals(prev => [...prev, removed]);
      toast.error(error.message);
    }
  };

  const updateQuantity = async (id: string, delta: number) => {
    const meal = meals.find(m => m.id === id);
    if (!meal) return;
    const newQty = Math.max(1, meal.quantity + delta);
    setMeals(prev => prev.map(m => m.id === id ? { ...m, quantity: newQty } : m));
    const { error } = await supabase.from("meals").update({ quantity: newQty }).eq("id", id);
    if (error) {
      setMeals(prev => prev.map(m => m.id === id ? { ...m, quantity: meal.quantity } : m));
      toast.error(error.message);
    }
  };

  const updateWater = async (glasses: number) => {
    if (!user) return;
    const previousGlasses = waterGlasses;
    setWaterGlasses(glasses);
    const today = new Date().toISOString().split("T")[0];
    const { error } = await supabase.from("water_intake").upsert({ patient_id: user.id, date: today, glasses }, { onConflict: "patient_id,date" });
    if (error) {
      setWaterGlasses(previousGlasses);
      toast.error(error.message);
    }
  };

  const caloriePct = calorieTarget.max > 0 ? Math.min((dayTotalCal / calorieTarget.max) * 100, 100) : 0;
  const isOverTarget = calorieTarget.max > 0 && dayTotalCal > calorieTarget.max;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fbfaf7] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fbfaf7] pb-40 text-[#263449]">
      <div className="relative h-[270px] overflow-hidden rounded-b-[2.5rem] bg-[#087b73] px-5 pt-8">
        <div className="relative z-10 flex items-start justify-between gap-3">
          <div className="pt-3">
            <h1 className="font-serif text-[26px] font-extrabold leading-none text-white">{t("meals.title")}</h1>
            <p className="mt-3 max-w-[150px] text-sm font-medium leading-5 text-white/90">
              {bn ? "আপনার খাবার ট্র্যাক করুন এবং সুস্থ থাকুন" : "Track your meals and stay on track"}
            </p>
          </div>
          <div className="flex items-center gap-2 rounded-full bg-[#f0f6f3] px-3 py-2 text-[#155b56]">
            <span className="text-[11px] font-bold">{bn ? "ফাস্টিং মোড" : "Fasting Mode"}</span>
            <Switch checked={fastingMode} onCheckedChange={handleFastingToggle} className="scale-75" />
          </div>
        </div>

        <img
          src="/meal-salad.png"
          alt=""
          aria-hidden="true"
          className="absolute right-[-10px] top-[68px] z-0 h-[138px] w-[220px] object-contain"
        />

        <div className="absolute bottom-4 left-5 right-5 z-10 flex gap-2">
          {mealTypes.map((mt) => {
            const MealTypeIcon = mealTypeIcons[mt.key] || UtensilsCrossed;
            return (
              <button key={mt.key} onClick={() => setActiveMealType(mt.key)}
                className={`flex min-w-0 flex-1 items-center justify-center gap-1 rounded-full py-3 text-[11px] font-bold transition-colors ${activeMealType === mt.key ? "bg-white text-[#155b56]" : "bg-[#2e8b83] text-white"}`}>
                <MealTypeIcon className="h-4 w-4 shrink-0" />
                {bn ? mt.labelBn : mt.labelEn}
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-4 px-5 pt-7">
        <div className="rounded-[1.7rem] border border-[#e7e8e5] bg-white px-5 py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 border-[#137d74] text-[#137d74]">
              <Target className="h-7 w-7" strokeWidth={1.8} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-bold text-[#263449]">{bn ? "দৈনিক ক্যালোরি লক্ষ্য" : "Daily Calorie Target"}</p>
                <p className="whitespace-nowrap text-xs font-bold text-[#167b73]">{calorieTarget.min} – {calorieTarget.max} {bn ? "কিলোক্যাল" : "kcal"}</p>
              </div>
              <div className="mt-4 h-3 w-full rounded-full bg-[#e8eeeb]">
                <motion.div className={`h-3 rounded-full transition-all duration-500 ${isOverTarget ? "bg-destructive" : "bg-[#167b73]"}`} initial={{ width: 0 }} animate={{ width: `${caloriePct}%` }} />
              </div>
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between gap-3">
            <p className={`text-xs font-bold ${isOverTarget ? "text-destructive" : "text-[#167b73]"}`}>{dayTotalCal} {bn ? "কিলোক্যাল খাওয়া হয়েছে" : "kcal consumed"}</p>
            <p className="text-xs text-[#596174]">{bn ? `বাকি: ${Math.max(0, calorieTarget.max - dayTotalCal)} কিলোক্যাল` : `${Math.max(0, calorieTarget.max - dayTotalCal)} kcal remaining`}</p>
          </div>
        </div>

        <div className="flex items-center justify-between rounded-[1.7rem] border border-[#e7e8e5] bg-white px-5 py-4">
          <div>
            <p className="text-sm font-medium text-[#596174]">{bn ? "আজকের মোট" : "Today's Total"}</p>
            <p className="mt-1 text-3xl font-extrabold leading-none text-[#13233a]">{dayTotalCal} <span className="text-sm font-bold text-[#596174]">kcal</span></p>
          </div>
          <motion.button onClick={() => setShowCustom(true)} className="flex h-12 w-12 items-center justify-center rounded-full bg-[#087b73]" whileTap={{ scale: 0.92 }}>
            <Plus className="h-7 w-7 text-white" strokeWidth={1.7} />
          </motion.button>
        </div>

        <div className="rounded-[1.7rem] border border-[#e7e8e5] bg-white px-5 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Droplets className="h-5 w-5 text-[#087b73]" strokeWidth={1.7} />
              <p className="text-sm font-bold text-[#263449]">{bn ? "পানি পান" : "Water Intake"}</p>
            </div>
            <p className="text-sm text-[#596174]">{waterGlasses}/{waterGoal} {bn ? "গ্লাস" : "glasses"}</p>
          </div>
          <div className="mt-4 flex items-center gap-1.5">
            {Array.from({ length: waterGoal }).map((_, i) => (
              <button key={i} onClick={() => updateWater(waterGlasses === i + 1 ? i : i + 1)}
                 className={`flex aspect-square min-w-0 flex-1 items-center justify-center rounded-full text-sm transition-colors ${i < waterGlasses ? "bg-[#d8f2ed] text-[#087b73]" : "bg-[#edf1f2] text-[#596174]"}`}>
                <Droplets className="h-4 w-4" strokeWidth={1.7} />
              </button>
            ))}
          </div>
          <div className="mt-4 h-2.5 w-full rounded-full bg-[#e8eeeb]">
            <div className="h-2.5 rounded-full bg-[#167b73] transition-all duration-500" style={{ width: `${(waterGlasses / waterGoal) * 100}%` }} />
          </div>
        </div>
      </div>

      <div className="mt-6 space-y-2 px-6">
        <p className="text-sm font-semibold uppercase tracking-wide text-[#596174]">{bn ? "লগ করা খাবার" : "Logged Meals"}</p>
        {currentMeals.map((meal) => (
          <motion.div key={meal.id} className="flex items-center gap-3 rounded-2xl border border-[#e7e8e5] bg-white p-3" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#d8f2ed]">
              <UtensilsCrossed className="h-5 w-5 text-[#087b73]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="truncate text-sm font-bold">{bn && meal.name_bn ? meal.name_bn : meal.name}</p>
              <div className="flex items-center gap-2 mt-0.5">
                <Clock className="h-3 w-3 text-muted-foreground" />
                <span className="text-[10px] text-muted-foreground">{new Date(meal.logged_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <button onClick={() => updateQuantity(meal.id, -1)} className="h-6 w-6 rounded-lg bg-muted flex items-center justify-center">
                <Minus className="h-3 w-3 text-muted-foreground" />
              </button>
              <span className="text-xs font-extrabold text-foreground w-5 text-center">{meal.quantity}</span>
              <button onClick={() => updateQuantity(meal.id, 1)} className="h-6 w-6 rounded-lg bg-primary/10 flex items-center justify-center">
                <Plus className="h-3 w-3 text-primary" />
              </button>
            </div>
            <span className="text-xs font-bold text-primary whitespace-nowrap">{meal.calories * meal.quantity} kcal</span>
            <button onClick={() => removeMeal(meal.id)} className="p-1">
              <X className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          </motion.div>
        ))}
        {currentMeals.length === 0 && (
          <div className="py-14 text-center text-sm text-[#596174]">{bn ? "এখনো কোনো খাবার লগ করা হয়নি" : "No meals logged yet"}</div>
        )}
      </div>

      {showCustom && (
        <motion.div className="fixed inset-0 z-50 flex items-center justify-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="absolute inset-0 bg-foreground/30" onClick={() => setShowCustom(false)} />
           <motion.div className="relative w-[90%] max-w-md rounded-2xl border border-[#e2ebe4] bg-card p-5" style={{ maxHeight: "85vh", overflowY: "auto" }} initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", damping: 25 }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-extrabold text-base">{bn ? "খাবার যোগ করুন" : "Add Meal"}</h3>
              <button onClick={() => setShowCustom(false)}><X className="h-5 w-5 text-muted-foreground" /></button>
            </div>

            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">{bn ? "কাস্টম খাবার" : "Custom Meal"}</p>
            <div className="space-y-3 mb-5">
              <div>
                <label className="text-xs font-bold text-muted-foreground mb-1 block">{bn ? "খাবারের নাম" : "Meal Name"}</label>
                <Input value={customName} onChange={(e) => setCustomName(e.target.value)} placeholder={bn ? "যেমন: ভাত, ডাল..." : "e.g. Rice, Dal..."} className="bg-background" />
              </div>
              <div>
                <label className="text-xs font-bold text-muted-foreground mb-1 block">{bn ? "ক্যালোরি" : "Calories"}</label>
                <Input type="number" value={customCal} onChange={(e) => setCustomCal(e.target.value)} placeholder={bn ? "যেমন: 200" : "e.g. 200"} className="bg-background" />
              </div>
              <Button onClick={addCustomMeal} className="w-full bg-[#2c7772] font-bold text-white" disabled={!customName.trim() || !customCal.trim()}>
                <Plus className="h-4 w-4 mr-2" />
                {bn ? "খাবার যোগ করুন" : "Add Meal"}
              </Button>
            </div>

            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">{bn ? "সাজেস্টেড" : "Suggested"}</p>
            <div className="space-y-2">
              {allFoods.map((s) => (
                <motion.button key={s.name} onClick={() => { addSuggestedMeal(s); setShowCustom(false); }}
                   className="flex w-full items-center justify-between rounded-xl bg-[#f1f6f2] px-3 py-2.5 text-left" whileTap={{ scale: 0.97 }}>
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                      <UtensilsCrossed className="h-4 w-4 text-primary" />
                    </div>
                    <p className="text-sm font-bold text-foreground">{bn ? s.nameBn : s.name}</p>
                  </div>
                  <span className="text-xs font-bold text-primary">{s.calories} kcal</span>
                </motion.button>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default MealsPage;
